import { styled, useTheme } from '@shared/components';
import { EditorContent } from '@tiptap/react';
import { forwardRef, useEffect, useMemo } from 'react';

import { useRichTextEditor } from '../../providers';

interface RichTextEditorContentProps {
  /**
   * The callback function when the editor is blurred
   */
  onBlur?: () => void;
  /**
   * The value of the editor
   */
  value?: string;
  /**
   * Number of rows to control the height of the editor
   */
  rows?: number;
}

const EditorWrapper = styled(EditorContent, { shouldForwardProp: (prop) => prop !== 'minHeight' })<{
  minHeight?: number;
}>(({ theme, minHeight }) => {
  return {
    '& p.is-editor-empty:first-of-type::before': {
      color: theme.palette.grey[400],
      content: 'attr(data-placeholder)',
      float: 'left',
      height: 0,
      pointerEvents: 'none',
    },

    '& .ProseMirror': {
      outline: 0,
      padding: theme.spacing(4, 4),
      backgroundColor: theme.palette.background.paper,
      borderBottomLeftRadius: theme.shape.rounded(2),
      borderBottomRightRadius: theme.shape.rounded(2),
      minHeight: minHeight,
    },

    '& .ProseMirror > *:first-of-type': {
      marginTop: theme.spacing(0),
    },

    '& .ProseMirror > *:last-child': {
      marginBottom: theme.spacing(0),
    },

    '& table': {
      borderCollapse: 'collapse',
      margin: theme.spacing(0),
      overflow: 'hidden',
      tableLayout: 'fixed',
      width: '100%',

      '& td, & th': {
        border: `1px solid ${theme.palette.grey[300]}`,
        boxSizing: 'border-box',
        minWidth: '1em',
        padding: theme.spacing(0.75, 1),
        position: 'relative',
        verticalAlign: 'top',

        '& > *': {
          marginBottom: theme.spacing(0),
        },
      },

      '& th': {
        backgroundColor: theme.palette.grey[100],
        fontWeight: theme.typography.fontWeightBold,
        textAlign: 'left',
      },

      '& .selectedCell:after': {
        background: theme.palette.grey[200],
        content: '""',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        pointerEvents: 'none',
        position: 'absolute',
        zIndex: 2,
      },

      '& .column-resize-handle': {
        backgroundColor: theme.palette.primary.main,
        bottom: -2,
        pointerEvents: 'none',
        position: 'absolute',
        right: -2,
        top: 0,
        width: 4,
      },
    },

    '& .tableWrapper': {
      margin: theme.spacing(1.5, 0),
      overflowX: 'auto',
    },

    '& .resize-cursor': {
      cursor: 'col-resize',
    },
  };
});

const RichTextEditorContent = forwardRef<HTMLDivElement, RichTextEditorContentProps>(
  (props: RichTextEditorContentProps, ref) => {
    const { onBlur, value, rows = 1 } = props;
    const { editor } = useRichTextEditor();
    const theme = useTheme();

    const minHeight = useMemo(() => {
      const fontSize =
        typeof theme.typography.body1.fontSize === 'number'
          ? theme.typography.body1.fontSize
          : theme.typography.htmlFontSize;
      const lineHeight =
        typeof theme.typography.body1.lineHeight === 'number'
          ? theme.typography.body1.lineHeight
          : 1.5;
      const spacing = parseInt(theme.spacing(4));

      return (
        fontSize * lineHeight * rows + // line height
        spacing * 2 + // padding
        spacing * (rows - 1) // margin between rows
      );
    }, [rows]);

    useEffect(() => {
      if (editor && value !== editor.getHTML()) {
        // queueMicrotask is used to prevent error during first render:
        // "flushSync was called from inside a lifecycle method."
        // Ref: https://github.com/ueberdosis/tiptap/issues/3580#issuecomment-1718048660
        queueMicrotask(() => {
          editor.commands.setContent(value ?? '');
        });
      }
    }, [editor, value]);

    useEffect(() => {
      const handleBlur = () => {
        onBlur?.();
      };

      editor?.on('blur', handleBlur);
      return () => {
        editor?.off('blur', handleBlur);
      };
    }, [editor, onBlur]);

    useEffect(() => {
      return () => {
        editor?.destroy();
      };
    }, [editor]);

    if (!editor) {
      return null;
    }

    return <EditorWrapper ref={ref} editor={editor} minHeight={minHeight} />;
  },
);

RichTextEditorContent.displayName = 'RichTextEditorContent';

export { RichTextEditorContent };
